import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import type { TablePlayer } from '../net/transport.js';

interface Props {
  /** Masada oturanlar, katılım sırasıyla — bot koltukları dahil. */
  players: TablePlayer[];
  /** Ben — kendi plakam altta dursun diye. */
  me: string;
  /** Masa doldu; maçın başlayacağı mutlak an. Yoksa hâlâ bekleniyor. */
  startsAt: number | null;
  /** Masaya ilk oturan ben miyim — boş koltuğa bot ekleyebilen tek kişi. */
  isHost?: boolean;
  /** Bot koltuğu sayısını ayarlar (mutlak değer). */
  onBots?: ((count: number) => void) | undefined;
  onLeave(): void;
}

const POSITIONS = ['bottom', 'right', 'top', 'left'] as const;

/**
 * Oturdun ama kartlar dağıtılmadı: masa görünümü, kimin oturduğu belli.
 *
 * Ayrı bir "bekleme kartı" ekranı denendi ve bırakıldı — oyuncu masaya
 * oturduğunu görmeden bekliyordu ve kimlerin geldiğini bilmiyordu. Burada
 * oyundaki masanın aynısı çiziliyor, yalnızca kart yok.
 *
 * Boş koltuklar YÖNETİCİYE (masaya ilk oturan) tıklanabilir geliyor: dokunulan
 * koltuk bota dönüşüyor, bota tekrar dokununca geri boşalıyor. Dört koltuk
 * dolunca maç normal geri sayımıyla başlıyor — yani "botlarla başlat" diye ayrı
 * bir düğme yok, masa doldurulunca zaten başlıyor.
 */
export function WaitingTable({ players, me, startsAt, isHost = false, onBots, onLeave }: Props) {
  const seconds = useCountdown(startsAt);

  // Kendimi başa al ki altta oturayım; gerisi katılım sırasıyla saat yönünde.
  const mine = players.findIndex((p) => !p.bot && p.name === me);
  const ordered = mine >= 0 ? [...players.slice(mine), ...players.slice(0, mine)] : [...players];

  const botCount = players.filter((p) => p.bot).length;
  const canEdit = isHost && onBots !== undefined && seconds === null;

  return (
    <div className="table">
      <div className="board">
        <button className="board__quit" onClick={onLeave} title="Masadan kalk" type="button">
          ✕
        </button>

        <div className="board__play">
          {POSITIONS.map((position, index) => {
            const player = ordered[index];
            const empty = player === undefined;

            /**
             * Boş koltuk → bot ekle, bot koltuğu → geri al.
             *
             * Sayı MUTLAK gönderiliyor: "bir tane daha" demek yerine masadaki
             * toplam bot sayısı bildiriliyor, böylece iki hızlı dokunuş
             * birbirini ezmiyor.
             */
            const action = !canEdit
              ? null
              : empty
                ? { label: 'Bot ekle', next: botCount + 1 }
                : player.bot
                  ? { label: 'Botu kaldır', next: botCount - 1 }
                  : null;

            const plate = (
              <div
                className={`seat__plate ${empty ? 'seat__plate--empty' : ''} ${
                  player?.bot ? 'seat__plate--bot' : ''
                }`}
              >
                <span className="seat__avatar">
                  {empty ? (action ? '+' : '') : player.name.slice(0, 1).toUpperCase()}
                </span>
                {!empty && (
                  <span className="seat__info">
                    <span className="seat__name">
                      <span className="seat__name-text">{player.name}</span>
                    </span>
                    {player.bot && <span className="tag tag--bot">bot</span>}
                  </span>
                )}
                {empty && action && <span className="seat__hint">Bot ekle</span>}
              </div>
            );

            return (
              <div key={position} className={`seat seat--${position} seat--waiting`}>
                {action ? (
                  <button
                    type="button"
                    className="seat__button"
                    onClick={() => onBots!(action.next)}
                    aria-label={action.label}
                    title={action.label}
                  >
                    {plate}
                  </button>
                ) : (
                  plate
                )}
              </div>
            );
          })}

          {seconds !== null && (
            <div className="countdown">
              <motion.span
                key={seconds}
                className="countdown__num"
                initial={{ scale: 1.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 320, damping: 22 }}
              >
                {seconds}
              </motion.span>
              <span className="countdown__text">Oyun başlıyor</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Kalan saniye; sayım yoksa null. */
function useCountdown(startsAt: number | null): number | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (startsAt === null) return;
    const id = setInterval(() => setNow(Date.now()), 120);
    return () => clearInterval(id);
  }, [startsAt]);

  if (startsAt === null) return null;
  return Math.max(0, Math.ceil((startsAt - now) / 1000));
}
